import { Search, User } from "lucide-react";

export default function VisitorsCard({
  visitors,
  searchQuery,
  setSearchQuery,
  loadingVisitorId,
  onVisitorSelect,
  formatDate,
  draftStartDate,
  draftEndDate,
  setDraftStartDate,
  setDraftEndDate,
  onApply,
  onReset,
  isInvalidDateRange,
}) {
  const filteredVisitors = visitors.filter(
    (v) =>
      String(v.max_id || "").includes(searchQuery) ||
      (v.visitor_id || "").toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {/* HEADER */}
      <div className="p-6 border-b border-gray-200 flex flex-wrap items-center gap-4 justify-between">
        <h2 className="text-2xl font-bold text-gray-900">All Visitors</h2>

        <div className="flex flex-wrap items-center gap-3 ml-auto">
          {/* Start Date */}
          <input
            type="date"
            value={draftStartDate}
            onChange={(e) => setDraftStartDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />

          {/* End Date */}
          <input
            type="date"
            value={draftEndDate}
            onChange={(e) => setDraftEndDate(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
          />

          {/* Apply */}
          <button
            onClick={onApply}
            disabled={isInvalidDateRange()}
            className={`px-4 py-2 rounded-lg text-sm transition text-white ${
              isInvalidDateRange()
                ? "bg-indigo-300 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700"
            }`}
          >
            Apply
          </button>

          {/* Reset */}
          {(draftStartDate || draftEndDate) && (
            <button
              onClick={onReset}
              className="bg-red-500 text-white px-4 py-2 rounded-lg text-sm hover:bg-red-600 transition"
            >
              Reset
            </button>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search by ID..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
        </div>
      </div>

      {/* LIST */}
      <div className="max-h-[600px] overflow-y-auto">
        {filteredVisitors.length === 0 ? (
          <div className="p-12 text-center text-gray-500">
            <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg font-semibold">No visitors found</p>
            <p className="text-sm mt-1">
              Try adjusting the date range or search criteria.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredVisitors.map((v) => (
              <div
                key={v.visitor_id}
                onClick={() => onVisitorSelect(v.visitor_id)}
                className="p-6 hover:bg-gray-50 cursor-pointer transition"
              >
                {loadingVisitorId === v.visitor_id && (
                  <div className="text-sm text-indigo-600 font-semibold animate-pulse mb-2">
                    Loading journey…
                  </div>
                )}

                <div className="flex items-center justify-between">
                  <div className="flex items-center flex-1">
                    <div className="bg-indigo-100 p-3 rounded-full mr-4">
                      <User className="w-6 h-6 text-indigo-600" />
                    </div>
                    <div>
                      <p className="font-mono text-sm font-semibold">
                        {v.max_id}
                      </p>
                      <p className="text-sm text-gray-500">
                        Last Active: {formatDate(v.last_activity)}
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-6">
                    <Stat label="Sessions" value={v.session_count} />
                    <Stat label="Actions" value={v.action_count} />
                    <Stat label="Buy Clicks" value={v.buy_clicks} highlight />
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className="text-center">
      <p className={`text-xl font-bold ${highlight ? "text-green-600" : ""}`}>
        {value}
      </p>
      <p className="text-xs text-gray-500">{label}</p>
    </div>
  );
}
